import { Globe } from "lucide-react";

import {
  organizationIdTail,
  resolveOrganizationCell,
} from "@/modules/dashboard/users/utils/organizationCell";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import type { DirectoryEntry } from "@/shared/hooks/useOrganizationDirectory";
import { useTranslations } from "@/shared/lang/useTranslations";

export interface ApiKeyOrganizationCellProps {
  organizationId: string | null | undefined;
  entry: DirectoryEntry | undefined;
  /** The catalog preload is still running — every cell resolves together, so this is column-wide. */
  isDirectoryPending: boolean;
  /** This one id is being fetched by the per-id fallback. Only these resolve independently. */
  isIdPending: boolean;
}

/**
 * Which organization a key belongs to.
 *
 * It reuses the users module's `resolveOrganizationCell` — same four states, same rule that "we could not
 * find out" must never be rendered like "there is none" — but renders the `none` case **differently, and that
 * difference is the whole reason this component exists**. On a user, a null organization is a data defect:
 * the column is NOT NULL server-side. On an API key it is a legitimate and common state: `POST /api-keys`
 * takes the organization from the caller, and an INTERNAL user has none to give, so every key staff create is
 * global. Rendering it in destructive red, as the users table correctly does, would flag normal staff keys as
 * broken.
 */
export function ApiKeyOrganizationCell({
  organizationId,
  entry,
  isDirectoryPending,
  isIdPending,
}: ApiKeyOrganizationCellProps) {
  const { t } = useTranslations("settings");
  const { t: tUsers } = useTranslations("users");
  const view = resolveOrganizationCell(organizationId, entry);

  if (view.kind === "none") {
    return (
      <ColocaBadge variant="secondary" icon={Globe}>
        {t("access_apikeys_org_global")}
      </ColocaBadge>
    );
  }

  if (view.kind === "unknown" && (isDirectoryPending || isIdPending)) {
    return <ColocaSkeleton className="h-4 w-28" />;
  }

  if (view.kind === "unknown") {
    return (
      <span title={view.id} className="truncate font-mono text-xs italic text-muted-foreground">
        …{organizationIdTail(view.id)}
        <span className="ml-1 not-italic">({tUsers("org_unresolved")})</span>
      </span>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span title={view.name} className="truncate text-sm text-foreground">
        {view.name}
      </span>
      {view.kind === "root" && (
        <span className="text-xs text-muted-foreground">{tUsers("org_internal")}</span>
      )}
    </div>
  );
}
